import { captureError } from "@/lib/errors";

/**
 * Copy text to the clipboard from an async source.
 *
 * Safari requires clipboard writes to happen synchronously within a user
 * gesture. Passing a promise to `ClipboardItem` satisfies this constraint
 * because the `navigator.clipboard.write()` call itself is synchronous —
 * Safari resolves the blob promise internally.
 *
 * @see https://wolfgangrittner.dev/how-to-use-clipboard-api-in-firefox/
 */
export function copyAsync(textPromise: Promise<string>): Promise<void> {
  const write =
    typeof ClipboardItem !== "undefined" && navigator.clipboard.write
      ? navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": textPromise.then(
              (text) => new Blob([text], { type: "text/plain" }),
            ),
          }),
        ])
      : // Firefox / Chrome — async clipboard access works fine in promise chains.
        textPromise.then((text) => navigator.clipboard.writeText(text));

  return write.catch((err) => {
    captureError(err, { tags: { feature: "clipboard" } });
    throw err;
  });
}
